import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.css';
import { useOverlay } from './useOverlay';

export interface ModalProps {
  title: string;
  lead?: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}

/**
 * A dialog that is usable one-handed on a 360px screen, which is where most of
 * these are answered. Escape closes it, focus moves into it on open and the
 * backdrop is not a click target for anything destructive.
 *
 * The focus move happens ONCE, on mount. Tying it to `onClose` re-runs it on
 * every render, and the screens that open these dialogs re-render every second
 * while a timer is running, so focus would jump back to the first button
 * between keystrokes and the note field could never be filled in.
 */
export function Modal({ title, lead, onClose, children, footer }: ModalProps) {
  const titleId = useId();
  const box = useRef<HTMLDivElement>(null);

  // Kept in a ref so the Escape listener always calls the current handler
  // without the effect depending on its identity.
  const close = useRef(onClose);
  close.current = onClose;
  useOverlay();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close.current();
    };
    document.addEventListener('keydown', onKey);
    box.current?.querySelector<HTMLElement>('button, input, textarea')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /*
   * Drawn in the body, not where it was opened. Request of 6 October 2026: on
   * an iPhone the page's own scroller is a layer of its own, and a sheet inside
   * it sat under the tab bar, its buttons hidden behind it. In the body it is
   * above everything, as its z-index says. React still sees it where it was
   * written, so clicks and keys reach the same handlers as before.
   */
  return createPortal(
    <div className={styles.backdrop} onClick={() => close.current()}>
      <div
        className={styles.box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={box}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.head}>
          <h3 id={titleId}>{title}</h3>
          {lead !== undefined && <p>{lead}</p>}
        </div>
        <div className={styles.content}>{children}</div>
        <div className={styles.foot}>{footer}</div>
      </div>
    </div>,
    document.body,
  );
}

export { styles as modalStyles };
